import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { ProgressBar } from '../components/ProgressBar';
import { Badge } from '../components/Badge';
import { renderPromptToHtml } from '../lib/markdown';
import type { PublicQuestion } from '../components/QuestionView';

interface ReviewItem {
  question: PublicQuestion;
  selected: string[];
  correct: boolean;
  correctAnswerIds: string[];
  explanation: string | null;
}

interface Review {
  attemptId: string;
  phase: 'benchmark' | 'dojo';
  mode: 'timed' | 'coaching';
  scorePct?: number;
  passed?: boolean;
  perCategory?: Record<string, { correct: number; total: number }>;
  submittedAt?: string;
  items: ReviewItem[];
}

interface ProgressResp {
  progress: {
    candidateId: string;
    benchmarkPassed: boolean;
    dojoMastered: boolean;
    bestBenchmark?: number;
    bestDojo?: number;
    weakest?: string[];
    badges: string[];
  };
}

const CATEGORY_LABEL: Record<string, string> = {
  'stack-basics': 'Stack basics',
  arithmetic: 'Arithmetic',
  'strings-ascii': 'Strings & ASCII',
  navigation: 'Navigation',
  loops: 'Loops',
  conditionals: 'Conditionals',
  combined: 'Combined'
};

const CATEGORY_COACHING: Record<string, string> = {
  'stack-basics':
    'The stack is **last-in-first-out**: every push goes on top, every `.`/`$`/`,` consumes the top. `:` duplicates the top, `\\` swaps the top two, `$` discards. An empty stack always returns `0` — Wine never errors. If you missed stack questions, walk the program one command at a time and write the stack contents next to each step.',
  arithmetic:
    'Binary operators (`+ - * / %`) all follow the same rule: **pop a (top), pop b (next), push b OP a**. `+` and `*` are commutative, but for `-` `/` `%` the SECOND-to-top is the left operand. So `27-` computes `2 - 7 = -5`, not `7 - 2`. Integer division (`/`) truncates toward zero. Modulo (`%`) is great for testing multiples (e.g. `:7%` leaves 0 exactly when the top is a multiple of 7).',
  'strings-ascii':
    'String mode (`"…"`) pushes each character\'s ASCII code in reading order, so the LAST character ends up on top. To print a word in its natural order, write the string **reversed** inside the quotes. `,` prints the top as an ASCII character (no trailing space); `.` prints it as a number with a trailing space. Useful ASCII anchors: `A`=65, `a`=97, `0`=48, space=32.',
  navigation:
    'Wine programs live on a 2-D grid. Execution starts at the top-left going right. `<` `>` `^` `v` unconditionally change direction. Blank cells are no-ops — execution flows straight through them. To trace a program, walk it cell by cell and keep track of (position, direction, stack). Direction commands themselves take no stack arguments.',
  loops:
    'Loops in Wine are built from three pieces: a value on the stack to count from, a **body** that does work and updates the counter (typically `:` then `1-`), and an **exit condition** (a conditional arrow that sees 0 when done). A common pattern is `5 > :. 1- v / ^      ← / @` — duplicate, print, decrement, then peek the dup for the exit check.',
  conditionals:
    'The arrow commands `← → ↑ ↓` are conditional: they **peek** the top of the stack (do not pop), and route execution in the arrow direction if the top is **non-zero (truthy)**, or the **opposite** direction if zero (falsy). Because they peek, the value stays on the stack — if you want to consume it after the branch, follow with `$`.',
  combined:
    'These questions stitch together multiple features. The reliable approach is to trace mechanically: every line, write down the stack BEFORE and AFTER each command. Watch out for the `b OP a` order on subtraction/division/modulo, and remember that string mode and counted loops both push values that need to be popped in the right order.'
};

export function Results() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [review, setReview] = useState<Review | null>(null);
  const [progress, setProgress] = useState<ProgressResp['progress'] | null>(null);
  const [openCats, setOpenCats] = useState<Set<string>>(new Set());
  const [startingDojo, setStartingDojo] = useState(false);
  const [dojoError, setDojoError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.get<Review>(`/api/take/attempt/${id}/review`).then(setReview);
    api.get<ProgressResp>('/api/take/progress').then((p) => setProgress(p.progress));
  }, [id]);

  const itemsByCategory = useMemo(() => {
    const m = new Map<string, ReviewItem[]>();
    if (!review) return m;
    for (const item of review.items) {
      const k = item.question.category;
      const arr = m.get(k) ?? [];
      arr.push(item);
      m.set(k, arr);
    }
    return m;
  }, [review]);

  if (!review || !progress) return <p className="loading">Loading results…</p>;

  const toggleCat = (cat: string) =>
    setOpenCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  const correctCount = review.items.filter((i) => i.correct).length;
  const total = review.items.length;
  const phaseTitle = review.phase === 'benchmark' ? 'Benchmark' : 'Dojo';
  const threshold = review.phase === 'benchmark' ? 50 : 90;
  const passed = review.passed === true;
  const showDojoCta = review.phase === 'benchmark' && passed;

  const startDojo = async () => {
    setStartingDojo(true);
    setDojoError(null);
    try {
      const r = await api.post<{ attemptId: string }>('/api/take/start-dojo');
      navigate(`/attempt/${r.attemptId}`);
    } catch (e) {
      const body = (e as { body?: { message?: string; error?: string } }).body;
      setDojoError(body?.message ?? body?.error ?? 'Failed to start the Dojo.');
    } finally {
      setStartingDojo(false);
    }
  };

  return (
    <div className="page-results">
      <h1>{phaseTitle} — {passed ? 'Passed' : 'Result'}</h1>

      <section className="result-headline">
        <div className={`score ${passed ? 'score-passed' : 'score-pending'}`}>
          {review.scorePct ?? 0}%
        </div>
        <div>
          <p>
            You answered <strong>{correctCount}</strong> out of <strong>{total}</strong>{' '}
            correctly.
          </p>
          <p>
            {passed
              ? `Above the ${threshold}% threshold — nicely done.`
              : `Below the ${threshold}% threshold. Review the categories below, then practise in the Dojo.`}
          </p>
        </div>
      </section>

      {showDojoCta && (
        <section className="card dojo-cta">
          <h2>🥋 Ready for the Dojo?</h2>
          <p>
            Now that you've passed the Benchmark, you can step into the Dojo — a
            one-question-at-a-time coaching session that keeps drilling each category
            until you've got it right.
          </p>
          <div className="cta-row">
            <button className="btn btn-primary" disabled={startingDojo} onClick={startDojo}>
              {startingDojo ? 'Starting…' : 'Start the Dojo →'}
            </button>
          </div>
          {dojoError && <p className="error">{dojoError}</p>}
        </section>
      )}

      {review.perCategory && (
        <section>
          <h2>By category — click to expand</h2>
          {(Object.entries(review.perCategory) as [string, { correct: number; total: number }][])
            .filter(([, v]) => v.total > 0)
            .map(([cat, v]) => {
              const pct = v.total === 0 ? 0 : Math.round((v.correct / v.total) * 100);
              const perfect = v.correct === v.total;
              const open = openCats.has(cat);
              const items = itemsByCategory.get(cat) ?? [];
              if (perfect) {
                return (
                  <div key={cat} className="card category-card category-perfect">
                    <div className="category-toggle category-toggle-static">
                      <span className="category-toggle-name">
                        🏆 {CATEGORY_LABEL[cat] ?? cat}
                      </span>
                      <span className="category-toggle-score">
                        {v.correct}/{v.total} · 100% · perfect — no review needed
                      </span>
                    </div>
                    <ProgressBar value={pct} />
                  </div>
                );
              }
              return (
                <div key={cat} className={`card category-card ${open ? 'open' : ''}`}>
                  <button
                    className="category-toggle"
                    onClick={() => toggleCat(cat)}
                    aria-expanded={open}
                  >
                    <span className="category-toggle-name">
                      {open ? '▾' : '▸'} {CATEGORY_LABEL[cat] ?? cat}
                    </span>
                    <span className="category-toggle-score">
                      {v.correct}/{v.total} · {pct}%
                    </span>
                  </button>
                  <ProgressBar value={pct} />
                  {open && (
                    <div className="category-detail">
                      <div
                        className="coaching"
                        dangerouslySetInnerHTML={{
                          __html: renderPromptToHtml(CATEGORY_COACHING[cat] ?? '')
                        }}
                      />
                      {items.map((item, i) => (
                        <ReviewBlock key={item.question.id} index={i + 1} item={item} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </section>
      )}

      <section className="badges">
        <h2>Your badges</h2>
        {progress.badges.length === 0 ? (
          <p className="muted">No badges yet. Earn them by clearing the Dojo or passing the Benchmark.</p>
        ) : (
          <div className="badge-row">
            {progress.badges.map((b) => (
              <Badge key={b} kind={b} />
            ))}
          </div>
        )}
      </section>

      <div className="cta-row">
        <Link to="/me" className="btn btn-secondary">
          Dashboard
        </Link>
      </div>
    </div>
  );
}

function ReviewBlock({ item, index }: { item: ReviewItem; index: number }) {
  const { question, selected, correct, correctAnswerIds, explanation } = item;
  return (
    <div className={`review-block ${correct ? 'review-block-ok' : 'review-block-bad'}`}>
      <div className="review-block-head">
        <span>
          <strong>{correct ? '🏅' : '❌'}</strong> Question {index}{' '}
          <span className="muted">· {question.id}</span>
        </span>
        <span className={`review-status ${correct ? 'status-ok' : 'status-bad'}`}>
          {correct ? 'Correct' : 'Wrong'}
        </span>
      </div>
      <div
        className="question-prompt"
        dangerouslySetInnerHTML={{
          __html: renderPromptToHtml(question.prompt)
        }}
      />
      <h4>Options</h4>
      <OptionList
        options={question.options}
        selected={selected}
        correctIds={correctAnswerIds}
      />
      {!correct && selected.length === 0 && (
        <p className="muted">You didn't answer this question.</p>
      )}
      {explanation && (
        <>
          <h4>Explanation</h4>
          <p>{explanation}</p>
        </>
      )}
    </div>
  );
}

function OptionList({
  options,
  selected,
  correctIds
}: {
  options: PublicQuestion['options'];
  selected: string[];
  correctIds: string[];
}) {
  const isStructured = options.some((o) => o.stack !== undefined || o.output !== undefined);
  if (isStructured) {
    return (
      <table className="options-table">
        <thead>
          <tr>
            <th></th>
            <th>Resulting stack</th>
            <th>Output</th>
          </tr>
        </thead>
        <tbody>
          {options.map((o) => {
            const isCorrect = correctIds.includes(o.id);
            const isUser = selected.includes(o.id);
            const cls = [
              'option-row',
              isCorrect ? 'option-correct' : '',
              isUser && !isCorrect ? 'option-wrong' : ''
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <tr key={o.id} className={cls}>
                <td className="option-radio">
                  {isCorrect ? (
                    <span className="mark mark-ok">✓</span>
                  ) : isUser ? (
                    <span className="mark mark-bad">✗</span>
                  ) : null}
                </td>
                <td><code>{o.stack ?? ''}</code></td>
                <td><code>{o.output ?? ''}</code></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
  return (
    <ul className="options">
      {options.map((o) => {
        const isCorrect = correctIds.includes(o.id);
        const isUser = selected.includes(o.id);
        const cls = ['option', isCorrect ? 'option-correct' : '', isUser && !isCorrect ? 'option-wrong' : '']
          .filter(Boolean)
          .join(' ');
        return (
          <li key={o.id}>
            <span className={cls}>
              <code>{o.label ?? ''}</code>
              {isCorrect && <span className="mark mark-ok"> ✓</span>}
              {isUser && !isCorrect && <span className="mark mark-bad"> ✗</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
